import { z } from 'zod';
import { idSchema } from '../domain/primitives.js';
import { publicUserSchema } from '../domain/user.js';
/**
 * The user directory: how a client finds someone to talk to, and how it turns
 * the ids it already holds into names.
 *
 * Both endpoints return `PublicUser`, never `User` - see the note on
 * `publicUserSchema` for why an email must not travel this way.
 */
/** Below this, a query matches too much of the directory to be a search. */
export const USER_SEARCH_MIN_QUERY = 2;
/** Hard ceiling on one lookup, so a client cannot ask for the whole table. */
export const USER_LOOKUP_MAX_IDS = 100;
/**
 * Query for `GET /api/v1/users?q=&limit=`.
 *
 * Matching is a case-insensitive substring of the display name, plus an exact
 * email match - enough to find "the other Ada" when you know her address,
 * without the endpoint ever confirming an address it was not given.
 */
export const userSearchQuerySchema = z.object({
    q: z.string().trim().min(USER_SEARCH_MIN_QUERY).max(254),
    limit: z.coerce.number().int().min(1).max(25).default(10),
});
/**
 * Query for `GET /api/v1/users/lookup?ids=a,b,c`.
 *
 * A comma-separated list rather than repeated `ids=` parameters, so one channel's
 * membership resolves in a single short URL. Unknown ids are simply absent from
 * the response - a caller asking about a deleted account gets a shorter list,
 * not an error.
 */
export const userLookupQuerySchema = z.object({
    ids: z
        .string()
        .min(1)
        .transform((value) => value.split(',').map((id) => id.trim()))
        .pipe(z.array(idSchema).min(1).max(USER_LOOKUP_MAX_IDS)),
});
/** Validator for either response body. */
export const publicUserListSchema = z.array(publicUserSchema);
/** Most people one page of the directory listing returns. */
export const USER_DIRECTORY_PAGE_MAX = 50;
/**
 * Query for `GET /api/v1/users/directory?q=&limit=&cursor=` - everyone you can
 * start a conversation with, A to Z, a page at a time, so a person can be found
 * without knowing how their name is spelt.
 *
 * `q` narrows the list exactly as search does: a display-name fragment or a
 * complete email address. `cursor` is the opaque `nextCursor` of the page
 * before. Disabled accounts and the caller are never listed.
 */
export const userDirectoryQuerySchema = z.object({
    q: z
        .string()
        .trim()
        .max(254)
        .optional()
        .transform((value) => (value ? value : undefined)),
    limit: z.coerce.number().int().min(1).max(USER_DIRECTORY_PAGE_MAX).default(30),
    cursor: z.string().min(1).max(512).optional(),
});
//# sourceMappingURL=users.js.map